import { SectionShell, Panel, Note } from "@/components/dash/primitives";
import { ChartTip, CHART } from "@/components/dash/chartkit";
import { BunkerChart } from "@/components/sections/BunkerChart";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

function RiskBadge({ risk }) {
  const map = {
    "Medium": "bg-amber-100 text-amber-700",
    "Low near-term": "bg-emerald-100 text-emerald-700",
    "Low": "bg-emerald-100 text-emerald-700",
    "High": "bg-blue-100 text-blue-700",
  };
  return <span className={`font-num text-xs px-2 py-0.5 rounded-full ${map[risk] || "bg-slate-100 text-slate-600"}`}>{risk}</span>;
}

export default function Macro({ data, num, title }) {
  return (
    <SectionShell id="macro" num={num} title={title}>
      <p className="text-slate-700 leading-relaxed max-w-4xl mb-8">{data.intro}</p>

      <BunkerChart data={data} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6">
        {data.bunker_stats.map((s, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`bunker-stat-${i}`}>
            <div className="font-display text-3xl font-bold text-slate-900">{s.value}</div>
            <div className="font-num text-xs uppercase tracking-wider text-emerald-600 mt-1">{s.label}</div>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
      <Note>{data.bunker_note}</Note>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="table-factors">
          <div className="px-5 py-3.5 border-b border-slate-200 bg-slate-50">
            <h3 className="font-display font-semibold text-slate-800">Three structural factors to watch</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 font-num text-xs uppercase tracking-wider">
                <th className="px-5 py-2.5">Factor</th>
                <th className="px-5 py-2.5">Status</th>
                <th className="px-5 py-2.5 text-right">Risk</th>
              </tr>
            </thead>
            <tbody>
              {data.factors.map((f, i) => (
                <tr key={i} className={i % 2 ? "bg-slate-50" : ""}>
                  <td className="px-5 py-3 font-medium text-slate-800 align-top">{f.factor}</td>
                  <td className="px-5 py-3 text-slate-600 align-top">{f.status}</td>
                  <td className="px-5 py-3 text-right align-top"><RiskBadge risk={f.risk} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="table-suez">
          <div className="px-5 py-3.5 border-b border-slate-200 bg-slate-50">
            <h3 className="font-display font-semibold text-slate-800">{data.suez_title}</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 font-num text-xs uppercase tracking-wider">
                <th className="px-5 py-2.5">Scenario</th>
                <th className="px-5 py-2.5">Rate impact</th>
                <th className="px-5 py-2.5 text-right">Prob.</th>
              </tr>
            </thead>
            <tbody>
              {data.suez.map((s, i) => (
                <tr key={i} className={i % 2 ? "bg-slate-50" : ""}>
                  <td className="px-5 py-3 font-medium text-slate-800 align-top">{s.scenario}</td>
                  <td className="px-5 py-3 text-slate-600 align-top">{s.rate}</td>
                  <td className="px-5 py-3 text-right align-top"><RiskBadge risk={s.probability} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="font-num text-[10px] uppercase tracking-wider text-slate-400 px-5 py-3">{data.suez_source}</p>
        </div>
      </div>
      <Note>{data.orderbook_note}</Note>
    </SectionShell>
  );
}
